import { useEffect, useState, type CSSProperties } from "react";
import {
  getSEOMetrics,
  listReferrals,
  type SEOMetricsReport,
  type ReferralItem,
} from "../../lib/api";
import PublishingStudioTabs from "../../components/admin/PublishingStudioTabs";
import { useIsMobile } from "../../lib/useIsMobile";

interface VIPInvitePass {
  id: string;
  code: string;
  recipient_name: string;
  tier: "Founding Member" | "Industry Guest" | "Curator VIP" | "Private Reserve";
  perk: string;
  max_uses: number;
  used_count: number;
  expires_at: string;
  created_at: string;
  notes?: string;
}

export default function AdminSEO() {
  // Phone (<768): stack side-by-side panes (they overflowed the 343px content width).
  const isPhone = useIsMobile(768);
  // Phone only: long queries/paths scroll inside the card. Not on wider screens — removing the
  // card's min-content width there squeezed the queries table into an inner scroll at 1024.
  const tableCard = isPhone ? { ...styles.tableCard, overflowX: "auto" as const } : styles.tableCard;
  const [activeTab, setActiveTab] = useState<"search" | "referrals" | "invites">("search");
  const [seo, setSeo] = useState<SEOMetricsReport | null>(null);
  const [referrals, setReferrals] = useState<ReferralItem[]>([]);
  const [loading, setLoading] = useState(true);

  // VIP Invites State
  const [invites, setInvites] = useState<VIPInvitePass[]>([
    {
      id: "inv-1",
      code: "VIP-FOUNDER-8842",
      recipient_name: "Alexander Vance",
      tier: "Founding Member",
      perk: "Complimentary Curator Pour + Spirit Locker Preview",
      max_uses: 1,
      used_count: 0,
      expires_at: "2026-12-31",
      created_at: "2026-08-20",
      notes: "Private client referral from Chicago Club",
    },
    {
      id: "inv-2",
      code: "VIP-CURATOR-3910",
      recipient_name: "Elena Rostova",
      tier: "Curator VIP",
      perk: "Waived Booking Deposit + Priority Speakeasy Entry",
      max_uses: 2,
      used_count: 1,
      expires_at: "2026-10-15",
      created_at: "2026-08-15",
      notes: "Sommelier guest pass",
    },
    {
      id: "inv-3",
      code: "VIP-INDUSTRY-5120",
      recipient_name: "Marcus Chen",
      tier: "Industry Guest",
      perk: "Master Distiller Tasting Flight",
      max_uses: 1,
      used_count: 1,
      expires_at: "2026-08-22",
      created_at: "2026-08-01",
      notes: "Buffalo Trace distillery brand rep",
    },
  ]);

  // Create Invite Form Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");
  const [newCode, setNewCode] = useState("");
  const [newTier, setNewTier] = useState<VIPInvitePass["tier"]>("Curator VIP");
  const [newPerk, setNewPerk] = useState("Complimentary Curator Pour + Priority Entry");
  const [newMaxUses, setNewMaxUses] = useState(1);
  const [newExpires, setNewExpires] = useState("2026-12-31");
  const [newNotes, setNewNotes] = useState("");

  // Toast
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === "search") {
        const data = await getSEOMetrics();
        setSeo(data);
      } else if (activeTab === "referrals") {
        const rList = await listReferrals();
        setReferrals(rList || []);
      }
    } catch (err: any) {
      showToast(err.message || "Failed to load telemetry", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  function handleGenerateCode() {
    const randomHex = Math.floor(1000 + Math.random() * 9000);
    const prefix = newTier === "Founding Member" ? "FOUNDER" : newTier === "Industry Guest" ? "INDUSTRY" : "VIP";
    setNewCode(`VIP-${prefix}-${randomHex}`);
  }

  function handleCreateInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!newRecipient.trim() || !newCode.trim()) return;

    const newPass: VIPInvitePass = {
      id: `inv-${Date.now()}`,
      code: newCode.trim().toUpperCase(),
      recipient_name: newRecipient.trim(),
      tier: newTier,
      perk: newPerk,
      max_uses: newMaxUses,
      used_count: 0,
      expires_at: newExpires,
      created_at: new Date().toISOString().split("T")[0],
      notes: newNotes.trim() || undefined,
    };

    setInvites(prev => [newPass, ...prev]);
    setShowCreateModal(false);
    setNewRecipient("");
    setNewCode("");
    setNewNotes("");
    showToast(`Created VIP Invite Passcode ${newPass.code}`);
  }

  function handleCopyInvite(code: string, recipient: string) {
    const url = `https://sentinelroom.com/invite/${code}`;
    navigator.clipboard.writeText(url);
    showToast(`Copied private VIP invite link for ${recipient}!`);
  }

  function handleCopySMS(code: string, recipient: string, perk: string) {
    const text = `Good afternoon ${recipient}, The Sentinel Room cordially invites you to private speakeasy service. Present VIP pass ${code} for ${perk}. Reserve: https://sentinelroom.com/invite/${code}`;
    navigator.clipboard.writeText(text);
    showToast(`Copied VIP invitation SMS for ${recipient}!`);
  }

  return (
    <div style={styles.container}>
      <PublishingStudioTabs activeTab="seo" badgeCounts={{ seo: referrals.length + invites.length }} />
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Search Performance, Referrals &amp; VIP Invites</h1>
          <p style={styles.subtitle}>
            Google Search Console impression trends, ambassador referral tracking, and private single-use VIP invitation passcodes.
          </p>
        </div>
        {activeTab === "invites" && (
          <button
            type="button"
            style={styles.createInviteBtn}
            onClick={() => {
              handleGenerateCode();
              setShowCreateModal(true);
            }}
          >
            + Generate VIP Invite Pass
          </button>
        )}
      </div>

      {/* Mode Tabs */}
      <div style={styles.tabBar}>
        <button
          type="button"
          style={activeTab === "search" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("search")}
        >
          Google Search Console &amp; Discovery
        </button>
        <button
          type="button"
          style={activeTab === "referrals" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("referrals")}
        >
          Ambassador Referrals ({referrals.length})
        </button>
        <button
          type="button"
          style={activeTab === "invites" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("invites")}
        >
          🎟️ VIP Private Invites ({invites.length})
        </button>
      </div>

      {loading && activeTab !== "invites" ? (
        <div style={styles.loader}>Loading telemetry…</div>
      ) : activeTab === "search" && seo ? (
        <div>
          {/* KPI Row */}
          <div style={styles.kpiGrid}>
            <div style={styles.kpiCard}>
              <div style={styles.kpiLabel}>Weekly Impressions</div>
              <div style={styles.kpiValue}>{seo.weekly_impressions.toLocaleString()}</div>
              <div style={styles.kpiSub}>Google Organic Search</div>
            </div>
            <div style={styles.kpiCard}>
              <div style={styles.kpiLabel}>Weekly Clicks</div>
              <div style={{ ...styles.kpiValue, color: "var(--gold)" }}>{seo.weekly_clicks.toLocaleString()}</div>
              <div style={styles.kpiSub}>Direct Site Visits</div>
            </div>
            <div style={styles.kpiCard}>
              <div style={styles.kpiLabel}>Average Position</div>
              <div style={{ ...styles.kpiValue, color: "#27ae60" }}>#{seo.avg_position}</div>
              <div style={styles.kpiSub}>Across Target Keywords</div>
            </div>
            <div style={styles.kpiCard}>
              <div style={styles.kpiLabel}>Average CTR</div>
              <div style={styles.kpiValue}>{seo.avg_ctr}%</div>
              <div style={styles.kpiSub}>Click-through Rate</div>
            </div>
          </div>

          {/* Tables Row */}
          <div style={isPhone ? { ...styles.tablesGrid, gridTemplateColumns: "minmax(0, 1fr)" } : styles.tablesGrid}>
            {/* Top Queries */}
            <div style={tableCard}>
              <h3 style={styles.tableTitle}>Top Google Search Queries</h3>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Search Query</th>
                    <th style={styles.thRight}>Rank</th>
                    <th style={styles.thRight}>Impressions</th>
                    <th style={styles.thRight}>Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {seo.top_queries.map((q, idx) => (
                    <tr key={idx} style={styles.tr}>
                      <td style={styles.td}><strong>{q.query}</strong></td>
                      <td style={styles.tdRight}>#{q.position}</td>
                      <td style={styles.tdRight}>{q.impressions}</td>
                      <td style={{ ...styles.tdRight, color: "var(--gold)", fontWeight: 700 }}>{q.clicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Top Landing Pages */}
            <div style={tableCard}>
              <h3 style={styles.tableTitle}>Top Search Landing Pages</h3>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Path</th>
                    <th style={styles.thRight}>Impressions</th>
                    <th style={styles.thRight}>Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {seo.top_pages.map((p, idx) => (
                    <tr key={idx} style={styles.tr}>
                      <td style={styles.td}><code>{p.url}</code></td>
                      <td style={styles.tdRight}>{p.impressions}</td>
                      <td style={{ ...styles.tdRight, color: "var(--gold)", fontWeight: 700 }}>{p.clicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : activeTab === "referrals" ? (
        referrals.length === 0 ? (
          <div style={styles.emptyState}>No ambassador referral links active yet.</div>
        ) : (
          <div style={styles.referralList}>
            {referrals.map((r) => (
              <div key={r.id} style={styles.referralCard}>
                <div>
                  <h3 style={styles.refName}>{r.referrer_name}</h3>
                  <div style={styles.refCode}>Code: <code>{r.referral_code}</code></div>
                </div>
                <div style={styles.refStats}>
                  <div>Clicks: <strong>{r.clicks_count}</strong></div>
                  <div>Signups: <strong>{r.signups_count}</strong></div>
                  <div style={{ color: "var(--gold)" }}>Rewards: <strong>{r.rewards_earned}</strong></div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        /* VIP Private Invites Tab */
        <div style={styles.invitesContainer}>
          <div style={styles.invitesGrid}>
            {invites.map((inv) => {
              const isExpired = new Date(inv.expires_at) < new Date();
              const isFullyUsed = inv.used_count >= inv.max_uses;
              const statusColor = isFullyUsed ? "#8A7A66" : isExpired ? "#E05040" : "#27ae60";
              const statusLabel = isFullyUsed ? "REDEEMED" : isExpired ? "EXPIRED" : "ACTIVE";

              return (
                <div key={inv.id} style={styles.inviteCard}>
                  <div style={styles.inviteTop}>
                    <div>
                      <div style={styles.inviteRecipient}>{inv.recipient_name}</div>
                      <div style={styles.inviteTier}>{inv.tier}</div>
                    </div>
                    <span style={{ ...styles.statusBadge, border: `1px solid ${statusColor}`, color: statusColor }}>
                      {statusLabel}
                    </span>
                  </div>

                  <div style={styles.codeBox}>
                    <code style={styles.codeText}>{inv.code}</code>
                  </div>

                  <div style={styles.invitePerk}>
                    <strong>VIP Perk:</strong> {inv.perk}
                  </div>

                  {inv.notes && (
                    <div style={styles.inviteNotes}>
                      <em>Notes: {inv.notes}</em>
                    </div>
                  )}

                  <div style={styles.inviteMeta}>
                    <span>Uses: {inv.used_count} / {inv.max_uses}</span>
                    <span>Expires: {inv.expires_at}</span>
                  </div>

                  <div style={styles.inviteActions}>
                    <button
                      type="button"
                      style={styles.copyBtn}
                      onClick={() => handleCopyInvite(inv.code, inv.recipient_name)}
                    >
                      🔗 Copy Link
                    </button>
                    <button
                      type="button"
                      style={styles.copyBtn}
                      onClick={() => handleCopySMS(inv.code, inv.recipient_name, inv.perk)}
                    >
                      💬 Copy SMS
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ CREATE VIP INVITE MODAL ═══ */}
      {showCreateModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modalBody} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>Generate VIP Private Invite</h2>
                <p style={styles.modalSubtitle}>Create single-use access credentials for prospective members and VIP guests.</p>
              </div>
              <button style={styles.closeBtn} onClick={() => setShowCreateModal(false)}>✕</button>
            </div>

            <form onSubmit={handleCreateInvite} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Recipient Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Jonathan Reynolds"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  style={styles.input}
                  autoFocus
                />
              </div>

              <div style={styles.inputGroup}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <label style={styles.label}>VIP Passcode *</label>
                  <button type="button" onClick={handleGenerateCode} style={styles.regenBtn}>
                    🎲 Re-generate
                  </button>
                </div>
                <input
                  type="text"
                  required
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={isPhone ? { ...styles.twoCol, gridTemplateColumns: "minmax(0, 1fr)" } : styles.twoCol}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>VIP Tier</label>
                  <select
                    value={newTier}
                    onChange={(e: any) => {
                      setNewTier(e.target.value);
                      handleGenerateCode();
                    }}
                    style={styles.select}
                  >
                    <option value="Curator VIP">Curator VIP</option>
                    <option value="Founding Member">Founding Member</option>
                    <option value="Industry Guest">Industry Guest</option>
                    <option value="Private Reserve">Private Reserve</option>
                  </select>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Max Uses</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={newMaxUses}
                    onChange={(e) => setNewMaxUses(parseInt(e.target.value, 10) || 1)}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Included VIP Perk / Hospitality Privilege</label>
                <input
                  type="text"
                  value={newPerk}
                  onChange={(e) => setNewPerk(e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Expiration Date</label>
                <input
                  type="date"
                  value={newExpires}
                  onChange={(e) => setNewExpires(e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Internal Notes (Source / Referrer)</label>
                <input
                  type="text"
                  placeholder="e.g. Met at Bourbon Heritage Dinner"
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                <button type="button" style={styles.cancelBtn} onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" style={styles.saveBtn}>
                  Issue VIP Passcode
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text)" },
  subtitle: { fontSize: 13, color: "var(--muted)", margin: "4px 0 0" },
  tabBar: { display: "flex", gap: 10, borderBottom: "1px solid var(--border)", marginBottom: 20 },
  // minWidth "min-content": global.css gives phone buttons min-width 44px, which replaces a flex
  // item's content-size floor, so this wrapping label shrank past its own text (4d verifier).
  tab: { background: "none", border: "none", borderBottom: "2px solid transparent", color: "var(--muted)", padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", minWidth: "min-content" },
  tabActive: { background: "none", border: "none", borderBottom: "2px solid var(--gold)", color: "var(--gold)", padding: "10px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", minWidth: "min-content" },
  createInviteBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 6,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 24 },
  kpiCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "16px 20px" },
  kpiLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)", letterSpacing: 0.5 },
  kpiValue: { fontSize: 26, fontWeight: 800, color: "var(--text)", margin: "4px 0" },
  kpiSub: { fontSize: 11, color: "var(--muted)" },
  tablesGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 },
  tableCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 18 },
  tableTitle: { fontSize: 14, fontWeight: 700, color: "var(--gold)", margin: "0 0 14px", textTransform: "uppercase", letterSpacing: 0.5 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--muted)", fontSize: 11, textTransform: "uppercase" },
  thRight: { textAlign: "right", padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--muted)", fontSize: 11, textTransform: "uppercase" },
  tr: { borderBottom: "1px solid rgba(255,255,255,0.04)" },
  td: { padding: "10px 10px", color: "var(--text)" },
  tdRight: { textAlign: "right", padding: "10px 10px", color: "var(--text)" },
  referralList: { display: "grid", gap: 12 },
  referralCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center" },
  refName: { fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text)" },
  refCode: { fontSize: 12, color: "var(--muted)", marginTop: 2 },
  refStats: { display: "flex", gap: 20, fontSize: 13, color: "var(--text)" },
  invitesContainer: { marginTop: 10 },
  invitesGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 },
  inviteCard: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 10,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  inviteTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  inviteRecipient: { fontSize: 16, fontWeight: 700, color: "var(--text, #F0E6D4)" },
  inviteTier: { fontSize: 11, color: "var(--gold, #F9AF36)", fontWeight: 600, marginTop: 2 },
  statusBadge: {
    fontSize: 9,
    fontWeight: 800,
    padding: "2px 6px",
    borderRadius: 4,
    letterSpacing: 0.5,
  },
  codeBox: {
    background: "rgba(0,0,0,0.4)",
    border: "1px dashed rgba(249, 175, 54, 0.4)",
    borderRadius: 6,
    padding: "8px 12px",
    textAlign: "center",
  },
  codeText: {
    fontSize: 14,
    fontFamily: "monospace",
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
    letterSpacing: 1.5,
  },
  invitePerk: { fontSize: 12, color: "var(--text, #F0E6D4)", lineHeight: 1.4 },
  inviteNotes: { fontSize: 11, color: "var(--muted, #8A7A66)" },
  inviteMeta: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    borderTop: "1px solid rgba(255,255,255,0.05)",
    paddingTop: 8,
  },
  inviteActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    marginTop: 4,
  },
  copyBtn: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 4,
    color: "var(--text, #F0E6D4)",
    fontSize: 11,
    padding: "6px 10px",
    cursor: "pointer",
    fontWeight: 600,
  },
  loader: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 },
  emptyState: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 },
  toast: { position: "fixed", bottom: 20, right: 20, color: "#111", fontWeight: 700, padding: "10px 18px", borderRadius: 8, zIndex: 9999, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20,
  },
  modalBody: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 12,
    padding: 24,
    maxWidth: 480,
    width: "100%",
    boxSizing: "border-box",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    borderBottom: "1px solid var(--border, #2A1F14)",
    paddingBottom: 12,
  },
  modalTitle: { fontSize: 18, fontFamily: "Georgia, serif", color: "var(--gold, #F9AF36)", margin: "0 0 4px" },
  modalSubtitle: { fontSize: 11, color: "var(--muted, #8A7A66)", margin: 0 },
  closeBtn: { background: "none", border: "none", color: "var(--muted, #8A7A66)", fontSize: 16, cursor: "pointer" },
  form: { display: "flex", flexDirection: "column", gap: 12 },
  inputGroup: { display: "flex", flexDirection: "column", gap: 4 },
  twoCol: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 },
  label: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--muted, #8A7A66)", letterSpacing: 0.5 },
  input: {
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "8px 10px",
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
    outline: "none",
  },
  select: {
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "8px 10px",
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
    outline: "none",
  },
  regenBtn: {
    background: "none",
    border: "none",
    color: "var(--gold, #F9AF36)",
    fontSize: 11,
    cursor: "pointer",
    fontWeight: 600,
  },
  saveBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 5,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  cancelBtn: {
    background: "transparent",
    color: "var(--muted, #8A7A66)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "8px 16px",
    fontSize: 12,
    cursor: "pointer",
  },
};

